import { useLocale } from 'next-intl';
import { useState, MouseEvent } from 'react';

import { ContentTypeVariations } from '@/components/organisms';
import { fetchSavedTracks, fetchRecentlyListened } from '@/lib/api';
import {
  fetchAlbum,
  fetchPlaylist,
  fetchPodcast,
  type Album,
  type Playlist,
  type Podcast,
  type NewsItem,
  PersonalPlaylist,
  fetchPersonalPlaylist,
  FavoritesForController,
  RecentsForController,
  PlaylistTrack,
} from '@/lib/api';
import { Chapter } from '@/lib/contexts/media-controller/util';
import { useMediaController } from '@/lib/hooks/media-controller/useMediaController';
import { logger } from '@/lib/logger';

import type { UUID } from '@/types';
interface UsePlayAllProps {
  id?: string;
  mediaType: ContentTypeVariations;
  rawData?:
    | Album
    | Playlist
    | NewsItem
    | Podcast
    | PersonalPlaylist
    | FavoritesForController
    | RecentsForController
    | { id: string; chapters: Chapter[] };
  startIndex?: number;
  trackId?: number;
  episodeIndex?: number;
  overriddenTrackList?: PlaylistTrack[];
}
export const usePlayAll = ({
  id,
  mediaType,
  rawData,
  startIndex = 0,
  trackId,
  episodeIndex,
  overriddenTrackList,
}: UsePlayAllProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const locale = useLocale();
  const {
    queueUpCompilation,
    pauseMiniPlayer,
    resumeMiniPlayer,
    isPlaying,
    currentCompilationId,
    currentCompilationType,
    currentTrackId,
    trackIndex,
  } = useMediaController();
  const isFavoriteOrRecent =
    mediaType === ContentTypeVariations.FAVORITE_PLAYLIST ||
    mediaType === ContentTypeVariations.RECENT_PLAYLIST;

  const mediaTypeToCompilationTypeMap: Record<string, string> = {
    [ContentTypeVariations.FAVORITE_PLAYLIST]: 'personal_favourites',
    [ContentTypeVariations.RECENT_PLAYLIST]: 'personal_recently_viewed',
  };

  if (mediaType === ContentTypeVariations.PERSONAL_PLAYLIST) {
    id = id?.split('/')[1];
  }

  const normalizedMediaType =
    mediaTypeToCompilationTypeMap[mediaType] ?? mediaType;
  const isMusicTrackControl =
    trackId !== undefined && mediaType !== ContentTypeVariations.PODCAST;
  const isCurrentMusicTrack =
    isMusicTrackControl && Number(currentTrackId) === trackId;
  const isControllingCurrentItem = isMusicTrackControl
    ? isCurrentMusicTrack
    : isFavoriteOrRecent
      ? trackId
        ? Number(currentTrackId) === trackId
        : id
          ? currentCompilationType === normalizedMediaType
          : true
      : currentCompilationId !== undefined &&
        (mediaType === ContentTypeVariations.PODCAST && trackId
          ? currentCompilationId ===
              (rawData && 'id' in rawData
                ? rawData.id?.toString()
                : id?.toString()) && Number(currentTrackId) === trackId
          : mediaType === ContentTypeVariations.PODCAST &&
              typeof episodeIndex === 'number'
            ? currentCompilationId ===
                (rawData && 'id' in rawData
                  ? rawData.id?.toString()
                  : id?.toString()) &&
              (rawData ? episodeIndex === trackIndex : true)
            : (rawData
                ? currentCompilationId ===
                  ('id' in rawData
                    ? rawData.id?.toString()
                    : 'playListId' in rawData
                      ? rawData.playListId?.toString()
                      : 'news' in rawData
                        ? rawData.news?.newsId?.toString()
                        : undefined)
                : id !== undefined && currentCompilationId === id) &&
              (trackId ? Number(currentTrackId) === trackId : true));

  const isSameType = isMusicTrackControl
    ? isCurrentMusicTrack
    : currentCompilationType === normalizedMediaType;

  const processResponse = (
    response:
      | Playlist
      | Album
      | Podcast
      | PersonalPlaylist
      | FavoritesForController
      | RecentsForController
      | unknown,
    index: number,
  ) => {
    switch (mediaType) {
      case ContentTypeVariations.ALBUM:
        queueAlbum(response as Album, index);
        break;
      case ContentTypeVariations.PLAYLIST:
        queuePlaylist(response as Playlist, index);
        break;
      case ContentTypeVariations.PODCAST:
        queuePodcast(response as Podcast, index);
        break;
      case ContentTypeVariations.ARTICLE:
        queueArticle(response as NewsItem, index);
        break;
      case ContentTypeVariations.PERSONAL_PLAYLIST:
        queuePersonalPlaylist(response as PersonalPlaylist, index);
        break;
      case ContentTypeVariations.FAVORITE_PLAYLIST:
        queueFavoritePlaylist(response as FavoritesForController, index);
        break;
      case ContentTypeVariations.RECENT_PLAYLIST:
        queueRecentPlaylist(response as RecentsForController, index);
    }
  };

  const queuePlaylist = (playlist: Playlist, index: number) => {
    queueUpCompilation(playlist.id.toString() as UUID, playlist, index);
  };
  const queueAlbum = (track: Album, index: number) => {
    queueUpCompilation(track.id.toString() as UUID, track, index);
  };
  const queuePodcast = (podcast: Podcast, index: number) => {
    queueUpCompilation(
      podcast.id.toString() as UUID,
      podcast,
      episodeIndex ?? index,
    );
  };
  const queueArticle = (article: NewsItem, index: number) => {
    queueUpCompilation(
      article.news.newsId.toString() as UUID,
      article.news as unknown as Playlist,
      index,
    );
  };
  const queuePersonalPlaylist = (playlist: PersonalPlaylist, index: number) => {
    const effectivePlaylist = overriddenTrackList
      ? { ...playlist, playLists: overriddenTrackList }
      : playlist;

    queueUpCompilation(
      playlist.playListId.toString() as UUID,
      effectivePlaylist,
      index,
    );
  };

  const queueFavoritePlaylist = (
    playlist: FavoritesForController,
    index: number,
  ) => {
    queueUpCompilation('000' as UUID, playlist, index);
  };
  const queueRecentPlaylist = (
    playlist: RecentsForController,
    index: number,
  ) => {
    queueUpCompilation('000' as UUID, playlist, index);
  };
  const handleClick = async (
    e: MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLElement>,
    index = startIndex,
    newTrackId?: number,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    const isSameTrack =
      newTrackId !== undefined
        ? Number(currentTrackId) === newTrackId
        : trackId
          ? Number(currentTrackId) === trackId
          : true;
    const isCurrentMusicTrackFromRow =
      newTrackId !== undefined &&
      mediaType !== ContentTypeVariations.PODCAST &&
      isSameTrack;
    if (
      isSameTrack &&
      (isCurrentMusicTrackFromRow || (isControllingCurrentItem && isSameType))
    ) {
      if (isPlaying) {
        pauseMiniPlayer();
      } else {
        resumeMiniPlayer();
      }
      return;
    }
    if (!rawData) {
      setIsLoading(true);

      let response:
        | Playlist
        | Album
        | Podcast
        | PersonalPlaylist
        | FavoritesForController
        | RecentsForController
        | null;

      try {
        if (mediaType === ContentTypeVariations.FAVORITE_PLAYLIST) {
          const savedArray = await fetchSavedTracks(locale);
          response = {
            type: 'personal_favourites',
            tracks: savedArray.map(track => ({
              id: track.id,
              title: track.title,
              track_metadata: track.track_metadata,
            })),
          } as FavoritesForController;
        } else if (mediaType === ContentTypeVariations.RECENT_PLAYLIST) {
          const savedArray = await fetchRecentlyListened(locale, 10, 1);
          response = {
            type: 'personal_recently_viewed',
            tracks: savedArray.data.map(track => ({
              id: track.trackId,
              title: track.trackTitle,
              track_metadata: track.track_metadata,
            })),
          } as unknown as RecentsForController;
        } else {
          const callToBe =
            mediaType === ContentTypeVariations.ALBUM
              ? fetchAlbum
              : mediaType === ContentTypeVariations.PLAYLIST
                ? fetchPlaylist
                : mediaType === ContentTypeVariations.PODCAST
                  ? fetchPodcast
                  : mediaType === ContentTypeVariations.PERSONAL_PLAYLIST
                    ? fetchPersonalPlaylist
                    : null;
          if (!callToBe) throw new Error('Unhandled mediaType');
          if (id === undefined)
            throw new Error(`id is undefined for mediaType: ${mediaType}`);

          response =
            mediaType === ContentTypeVariations.PODCAST
              ? await (callToBe as typeof fetchPodcast)({
                  slugOrId: Number(id),
                  locale,
                })
              : await (
                  callToBe as
                    | typeof fetchAlbum
                    | typeof fetchPlaylist
                    | typeof fetchPersonalPlaylist
                )({ slugOrId: id, locale });
        }
        processResponse(response, index);
      } catch (error) {
        const typedError = error as Error & { digest?: string };
        logger.error(typedError.message, {
          source: 'nextjs-app-usePlayAll',
          digest: typedError.digest,
          stack: typedError.stack,
        });
      } finally {
        setIsLoading(false);
      }
    } else {
      processResponse(
        rawData as
          | Playlist
          | Album
          | Podcast
          | PersonalPlaylist
          | FavoritesForController
          | RecentsForController
          | unknown,
        index,
      );
    }
  };
  return {
    isLoading,
    isPlaying,
    isControllingCurrentItem,
    isSameType,
    handleClick,
  };
};
